import { useMemo, useState } from 'react';

export interface MarkdownField<T extends string> {
  key: T;
  heading: string;
}

export function MarkdownExchange<T extends string>({
  title,
  publicTemplateUrl,
  promptInstructions,
  fields,
  values,
  onImport,
}: {
  title: string;
  publicTemplateUrl: string;
  promptInstructions: readonly string[];
  fields: readonly MarkdownField<T>[];
  values: Partial<Record<T, string | undefined>>;
  onImport: (values: Partial<Record<T, string>>) => void;
}) {
  const generated = useMemo(
    () => toMarkdown(title, promptInstructions, fields, values),
    [title, promptInstructions, fields, values],
  );
  const [source, setSource] = useState(generated);
  const [status, setStatus] = useState('');
  return (
    <details className="panel markdown-exchange">
      <summary>Use with an AI assistant</summary>
      <p>
        Copy this structured Markdown into ChatGPT, Claude, Gemini, or another assistant. Ask it to
        preserve the headings, then paste the completed Markdown back here.
      </p>
      <p>
        <a href={publicTemplateUrl} target="_blank" rel="noreferrer">
          Open public .md template
        </a>
      </p>
      <label>
        Markdown
        <textarea rows={18} value={source} onChange={(event) => setSource(event.target.value)} />
      </label>
      <div className="actions">
        <button
          type="button"
          onClick={() => {
            void navigator.clipboard
              .writeText(source)
              .then(() => setStatus('Markdown copied'))
              .catch(() => setStatus('Copy failed; select the Markdown and copy it manually'));
          }}
        >
          Copy Markdown
        </button>
        <button
          type="button"
          onClick={() => {
            onImport(fromMarkdown(source, fields));
            setStatus('Recognized Markdown fields copied into the form');
          }}
        >
          Fill form from Markdown
        </button>
        <button type="button" onClick={() => setSource(generated)}>
          Refresh from form
        </button>
      </div>
      <p role="status">{status}</p>
    </details>
  );
}

export function toMarkdown<T extends string>(
  title: string,
  promptInstructions: readonly string[],
  fields: readonly MarkdownField<T>[],
  values: Partial<Record<T, string | undefined>>,
) {
  return [
    `# Prompt: Help us complete a ${title}`,
    '',
    ...promptInstructions.flatMap((instruction) => [instruction, '']),
    'Do not include real confidential or personal information. PRIVATE game information may be used, but anything pasted into an external assistant is handled under that service’s policies.',
    '',
    'Use relevant context from our conversation. If important information is missing, ask no more than three focused questions before producing the final response.',
    '',
    'When ready, return only the completed Markdown under "Required output format." Keep every heading exactly as written. Do not use a code fence and do not add commentary before or after the template.',
    '',
    '---',
    '',
    '# Required output format',
    '',
    `# ${title}`,
    '',
    ...fields.flatMap((field) => [
      `## ${field.heading}`,
      '',
      values[field.key]?.trim() || '[Enter response]',
      '',
    ]),
  ].join('\n');
}

export function fromMarkdown<T extends string>(
  source: string,
  fields: readonly MarkdownField<T>[],
) {
  const headings = new Map(fields.map((field) => [field.heading.toLowerCase(), field.key]));
  const result: Partial<Record<T, string>> = {};
  const sections = source.split(/^##\s+/gm).slice(1);
  for (const section of sections) {
    const [heading = '', ...body] = section.split('\n');
    const key = headings.get(heading.trim().toLowerCase());
    const value = body.join('\n').trim();
    if (key && value && !/^\[[^\]]+\]$/.test(value)) result[key] = value;
  }
  return result;
}
